// AGC: FieldDetail - Individual field details with plots list
// AGC: ⚠️ [DEPRECATED] This component uses MOCK DATA from data/fields.ts
// AGC: ⚠️ [DEPRECATED] Use LotesList or LotPage instead for production data
// AGC: ⚠️ [DEPRECATED] Only visible in dev mode (DEV_FLAGS.ENABLE_LEGACY_SCREENS)
import React, { useEffect } from 'react'
import { Plus, MapPin, Calendar, Droplets, AlertTriangle } from 'lucide-react'
import { GlassCard, StatusBadge, Button, ProgressBar } from '../shared'
import { formatArea, formatDate } from '@droidfarm/shared'
import { getFieldById } from '../../data/fields'
import { CROP_TYPES, getCropDisplayInfo, type Screen, type Plot } from '../../types'
import { DEV_FLAGS } from '../../services/lotServices'

interface FieldDetailProps {
  fieldId: string
  onNavigate: (screen: Screen, data?: { fieldId?: string; plotId?: string }) => void
}

/**
 * AGC: FieldDetail - Shows field overview with all plots
 * AGC: ⚠️ DEPRECATED - This component uses mock data from data/fields.ts
 * AGC: Use LotesList or LotPage instead
 */
export const FieldDetail: React.FC<FieldDetailProps> = ({ fieldId, onNavigate }) => {
  const field = getFieldById(fieldId)

  // AGC: [SOURCE_OF_TRUTH] Redirect to LotesList in production mode
  useEffect(() => {
    if (!DEV_FLAGS.ENABLE_LEGACY_SCREENS) {
      console.warn('⚠️ AGC: [DEPRECATED] FieldDetail is deprecated. Redirecting to lotes-list...')
      onNavigate('lotes-list')
    }
  }, [onNavigate])

  // AGC: Show deprecation warning in dev mode
  if (DEV_FLAGS.ENABLE_LEGACY_SCREENS) {
    console.warn('⚠️ AGC: [DEPRECATED] FieldDetail is using mock data from data/fields.ts. Use LotesList instead.')
  }

  if (!field) {
    return (
      <div className="w-full bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Campo não encontrado</p>
      </div>
    )
  }

  // AGC: Calculate field statistics
  const stats = {
    totalPlots: field.plots.length,
    irrigatedArea: field.plots.filter(p => p.irrigated).reduce((acc, p) => acc + p.area, 0),
    totalArea: field.totalArea,
  }

  // AGC: Group plots by crop type (handles custom/learned crop types)
  const plotsByCrop = field.plots.reduce((acc, plot) => {
    const cropDisplay = getCropDisplayInfo(plot.cropType)
    const key = cropDisplay.name
    if (!acc[key]) acc[key] = []
    acc[key].push(plot)
    return acc
  }, {} as Record<string, Plot[]>)

  return (
    <div className="w-full bg-[#F4F5F7] dark:bg-slate-900">
      {/* AGC: Field Header - Simple header (NOT sticky, TopNav from MainMenu is already sticky) */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-50">{field.name}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{field.location}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={<Plus className="w-5 h-5" />}
            onClick={() => onNavigate('new-plot-1', { fieldId: field.id })}
          >
            Talhão
          </Button>
        </div>
      </div>

      {/* AGC: Field Stats */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <GlassCard className="text-center">
            <p className="text-2xl font-bold text-[#0B5F4A]">{formatArea(stats.totalArea)}</p>
            <p className="text-xs text-gray-500 mt-1">Área Total</p>
          </GlassCard>
          <GlassCard className="text-center">
            <p className="text-2xl font-bold text-[#0B5F4A]">{stats.totalPlots}</p>
            <p className="text-xs text-gray-500 mt-1">Talhões</p>
          </GlassCard>
          <GlassCard className="text-center">
            <p className="text-2xl font-bold text-[#0B5F4A]">{formatArea(stats.irrigatedArea)}</p>
            <p className="text-xs text-gray-500 mt-1">Irrigado</p>
          </GlassCard>
        </div>
      </div>

      {/* AGC: Plots by Crop Type */}
      <div className="px-4 pb-24">
        {Object.entries(plotsByCrop).map(([cropName, plots]) => (
          <div key={cropName} className="mb-6">
            <h3 className="text-gray-900 mb-3 flex items-center gap-2">
              {CROP_TYPES.find(c => c.name === cropName)?.icon}
              {cropName}
              <span className="text-sm text-gray-500 font-normal">
                ({plots.length} {plots.length === 1 ? 'talhão' : 'talhões'})
              </span>
            </h3>
            
            <div className="space-y-3">
              {plots.map((plot) => (
                <PlotCard key={plot.id} plot={plot} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * AGC: Plot Card Component
 */
interface PlotCardProps {
  plot: Plot
}

const PlotCard: React.FC<PlotCardProps> = ({ plot }) => {
  // AGC: Get crop display info (handles custom/learned crop types)
  const cropDisplay = getCropDisplayInfo(plot.cropType)
  
  // AGC: Growth stage labels
  const stageLabels: Record<string, string> = {
    plantio: 'Plantio',
    germinacao: 'Germinação',
    vegetativo: 'Vegetativo',
    floracao: 'Floração',
    frutificacao: 'Frutificação',
    maturacao: 'Maturação',
    colheita: 'Colheita',
  }

  // AGC: Health status mapping
  const healthMapping: Record<string, 'completed' | 'in-progress' | 'pending' | 'overdue'> = {
    excelente: 'completed',
    bom: 'completed',
    regular: 'in-progress',
    ruim: 'pending',
    critico: 'overdue',
  }

  // AGC: Calculate progress based on growth stage
  const stageProgress: Record<string, number> = {
    plantio: 10,
    germinacao: 20,
    vegetativo: 40,
    floracao: 60,
    frutificacao: 75,
    maturacao: 90,
    colheita: 100,
  }

  return (
    <GlassCard hover>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium text-gray-900">{plot.name}</h3>
          <p className="text-sm text-gray-500">
            {cropDisplay.name} {plot.variety && `• ${plot.variety}`}
          </p>
        </div>
        <StatusBadge 
          status={healthMapping[plot.healthStatus]} 
          label={plot.healthStatus.charAt(0).toUpperCase() + plot.healthStatus.slice(1)}
        />
      </div>

      {/* AGC: Plot Info */}
      <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-600">
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          {formatArea(plot.area)}
        </span>
        {plot.plantingDate && (
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(plot.plantingDate)}
          </span>
        )}
        {plot.irrigated && (
          <span className="flex items-center gap-1 text-blue-600">
            <Droplets className="w-4 h-4" />
            Irrigado
          </span>
        )}
      </div>

      {/* AGC: Growth Stage Progress */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500">Estágio: {stageLabels[plot.growthStage]}</span>
          <span className="text-gray-500">{(stageProgress[plot.growthStage] || 0)}%</span>
        </div>
        <ProgressBar 
          progress={stageProgress[plot.growthStage] || 0} 
          size="sm"
          color={plot.healthStatus === 'excelente' || plot.healthStatus === 'bom' ? 'primary' : 'warning'}
        />
      </div>

      {/* AGC: Notes */}
      {plot.notes && (
        <p className="mt-3 text-sm text-gray-500 italic border-l-2 border-yellow-400 pl-2">
          {plot.notes}
        </p>
      )}
    </GlassCard>
  )
}

export default FieldDetail



